import { GetAway } from './GetAway'
import type { GetAwayAvailability } from './GetAwayAvailability'

export class GetAwayWithAvailability {
  constructor(
    public uuid: string,
    public getAway: GetAway,
    public availability: GetAwayAvailability | null,
  ) {}

  public static from(getAway: GetAway, availability: GetAwayAvailability | null): GetAwayWithAvailability {
    return new GetAwayWithAvailability(
      getAway.uuid,
      getAway,
      availability,
    )
  }
}

export class GetAwayWithAvailabilityCollection {
  private withAvailability: Array<GetAwayWithAvailability>
  private withoutAvailability: Array<GetAwayWithAvailability>

  constructor(getAways: Array<GetAwayWithAvailability>) {
    this.withAvailability = []
    this.withoutAvailability = []

    getAways.forEach((a) => {
      if (a.availability && a.availability.bestRate) {
        this.withAvailability.push(a)
        return
      }

      this.withoutAvailability.push(a)
    })
  }

  public getWithAvailability() {
    return this.withAvailability
  }

  public getWithoutAvailability() {
    return this.withoutAvailability
  }

  public get() {
    return this.withAvailability.concat(this.withoutAvailability)
  }

  public filterByHotelStarCount(stars: Array<number>) {
    function filterByStars(ga: GetAwayWithAvailability) {
      const hotelStars = ga.getAway.hotel.category

      return stars.includes(hotelStars)
    }

    this.withAvailability = this.withAvailability.filter(filterByStars)
    this.withoutAvailability = this.withoutAvailability.filter(filterByStars)
  }

  public filterByIncludesTagUUIDs(tagUUIDs: Array<string>) {
    function filterByTagUUIDs(ga: GetAwayWithAvailability): boolean {
      for (const uuid of tagUUIDs) {
        if (GetAway.hasTagUUID(ga.getAway, uuid)) {
          return true
        }
      }

      return false
    }

    this.withAvailability = this.withAvailability.filter(filterByTagUUIDs)
    this.withoutAvailability = this.withoutAvailability.filter(filterByTagUUIDs)
  }

  public sortByCheaper() {
    this.withAvailability = this.withAvailability
      .sort((a, b) => {
        if (!a.availability || !a.availability.bestRate) {
          return 0
        }

        if (!b.availability || !b.availability.bestRate) {
          return 0
        }

        const rateA = a.availability.bestRate
        const rateB = b.availability.bestRate

        const amountA = rateA.basePriceWithDiscount.amount
        const amountB = rateB.basePriceWithDiscount.amount

        if (amountA < amountB) return -1
        if (amountA > amountB) return 1

        return 0
      })
  }

  public sortByMostExpensive() {
    this.sortByCheaper()
    this.withAvailability = this.withAvailability.reverse()
  }

  public filterByIncludesSpa() {
    this.withAvailability = this.filterByIncludesSpaImpl(this.withAvailability)
    this.withoutAvailability = this.filterByIncludesSpaImpl(this.withoutAvailability)
  }

  private filterByIncludesSpaImpl(list: Array<GetAwayWithAvailability>) {
    return list.filter((a) => a.getAway.includesSpa)
  }

  public spliceByIncludesSpa() {
    this.withAvailability = this.sortByIncludesSpaImpl(this.withAvailability)
    this.withoutAvailability = this.sortByIncludesSpaImpl(this.withoutAvailability)
  }

  private sortByIncludesSpaImpl(list: Array<GetAwayWithAvailability>) {
    const withSpa: Array<GetAwayWithAvailability> = []
    const withoutSpa: Array<GetAwayWithAvailability> = []

    list.forEach((a) => {
      if (a.getAway.includesSpa) {
        withSpa.push(a)
        return
      }

      withoutSpa.push(a)
    })

    return withSpa.concat(withoutSpa)
  }
}
